import React, { useRef, useState } from 'react';
import { CopyIcon, MailIcon, PhoneIcon, MapPinIcon } from 'lucide-react';
import { CONTACT } from '../../data/navigation';
interface FormState {
  name: string;
  company: string;
  email: string;
  phone: string;
  material: string;
  machine: string;
  horsepower: string;
  workholding: string;
  holderSize: string;
  goal: string;
  quantity: string;
  message: string;
}
const initialState: FormState = {
  name: '',
  company: '',
  email: '',
  phone: '',
  material: '',
  machine: '',
  horsepower: '',
  workholding: '',
  holderSize: '',
  goal: '',
  quantity: '',
  message: ''
};
const requiredFields: {
  key: keyof FormState;
  label: string;
}[] = [{
  key: 'name',
  label: 'Name'
}, {
  key: 'company',
  label: 'Company'
}, {
  key: 'email',
  label: 'Work email'
}, {
  key: 'material',
  label: 'Material'
}, {
  key: 'holderSize',
  label: 'Holder size'
}, {
  key: 'goal',
  label: 'Application goal'
}];
const fieldLabels: Record<keyof FormState, string> = {
  name: 'Name',
  company: 'Company',
  email: 'Work email',
  phone: 'Phone',
  material: 'Material',
  machine: 'Machine model',
  horsepower: 'Machine horsepower',
  workholding: 'Workholding / setup',
  holderSize: 'Holder size',
  goal: 'Application goal',
  quantity: 'Expected quantity',
  message: 'Message'
};
const inputClass = 'mt-2 block w-full min-h-[48px] border border-rule bg-white px-3.5 py-2.5 text-[14.5px] text-graphite transition-colors duration-150 ease-precise placeholder:text-graphite/40 focus:border-copper';
function buildRequestText(values: FormState): string {
  return (Object.keys(fieldLabels) as (keyof FormState)[]).filter(key => values[key].trim().length > 0).map(key => `${fieldLabels[key]}: ${values[key].trim()}`).join('\n');
}
export function ProductGuidance() {
  const [values, setValues] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const summaryRef = useRef<HTMLDivElement>(null);
  function validate(next: FormState) {
    const nextErrors: Partial<Record<keyof FormState, string>> = {};
    requiredFields.forEach(({
      key,
      label
    }) => {
      if (!next[key].trim()) nextErrors[key] = `${label} is required.`;
    });
    if (next.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(next.email)) {
      nextErrors.email = 'Enter a valid work email address.';
    }
    return nextErrors;
  }
  function update(key: keyof FormState, value: string) {
    const next = {
      ...values,
      [key]: value
    };
    setValues(next);
    setCopyState('idle');
    if (submitted) setErrors(validate(next));
  }
  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitted(true);
    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      requestAnimationFrame(() => summaryRef.current?.focus());
      return;
    }
    const subject = `Shear Geometry product guidance — ${values.company.trim()} (${values.material})`;
    const body = buildRequestText(values);
    const href = `mailto:${CONTACT.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body).slice(0, 1800)}`;
    window.location.href = href;
  }
  async function handleCopy() {
    const text = buildRequestText(values);
    try {
      await navigator.clipboard.writeText(text);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  }
  const errorList = requiredFields.filter(({
    key
  }) => errors[key]);
  return <section id="product-guidance" aria-labelledby="guidance-heading" className="w-full bg-navy" data-id="element-205" data-mp="31ujybb6etxry">
      <div className="mx-auto grid max-w-shell gap-12 px-5 py-16 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-20 lg:px-10 lg:py-24" data-id="element-206" data-mp="2e3nzhu3je2b7">
        <div data-id="element-207" data-mp="2a11eod5a9hni">
          <p className="flex items-center gap-3 font-mono text-[10px] tracking-label text-copper" data-id="element-208" data-mp="2msd5wy1e9ony">
            <span aria-hidden="true" className="h-px w-8 bg-copper" data-id="element-209" data-mp="2rrfznmbxc8gm" />
            APPLICATION SUPPORT
          </p>
          <h2 id="guidance-heading" className="mt-5 font-display text-[clamp(1.75rem,3.2vw,2.9rem)] font-medium leading-[1.1] text-white" data-id="element-210" data-mp="2wqstws54kht8">
            Tell us what you’re cutting
          </h2>
          <p className="rp-measure mt-6 text-[15.5px] leading-[1.7] text-aluminum" data-id="element-211" data-mp="36faw9dt1ywyz">
            Share the material, machine, horsepower, workholding, holder size,
            target depth of cut and feed, and expected quantity. Robertson
            Precision can help identify the most relevant published starting
            point and product family.
          </p>

          <dl className="mt-10 divide-y divide-white/10 border-y border-white/10" data-id="element-212" data-mp="1l39gdqs2vo4h">
            <div className="flex flex-col gap-1.5 py-4 sm:flex-row sm:items-center sm:gap-4" data-id="element-213" data-mp="1q22spfvmp932">
              <dt className="flex items-center gap-3 font-mono text-[10px] tracking-label text-aluminum/70 sm:w-40 sm:shrink-0" data-id="element-214" data-mp="1q3xc2pr94t51">
                <PhoneIcon aria-hidden="true" className="h-4 w-4 text-copper" data-id="element-215" data-mp="1drl8qna23iz9" />
                SALES
              </dt>
              <dd className="min-w-0" data-id="element-216" data-mp="18tjqu5od9ppd">
                <a href={CONTACT.phoneHref} data-analytics="phone-click" data-analytics-line="sales" data-analytics-location="product-guidance" className="font-mono text-[15px] text-white underline decoration-copper underline-offset-4 transition-colors duration-150 ease-precise hover:text-cta" data-id="element-217" data-mp="18qfdoisxbjmk">
                  {CONTACT.phoneLabel}
                </a>
              </dd>
            </div>
            <div className="flex flex-col gap-1.5 py-4 sm:flex-row sm:items-center sm:gap-4" data-id="element-218" data-mp="1mmj4s38i3but">
              <dt className="flex items-center gap-3 font-mono text-[10px] tracking-label text-aluminum/70 sm:w-40 sm:shrink-0" data-id="element-219" data-mp="g20266903jfz">
                <PhoneIcon aria-hidden="true" className="h-4 w-4 text-copper" data-id="element-220" data-mp="2zb87ip7vygle" />
                TECHNICAL
              </dt>
              <dd className="min-w-0" data-id="element-221" data-mp="33bim2q9qufbv">
                <a href={CONTACT.techPhoneHref} data-analytics="phone-click" data-analytics-line="technical" data-analytics-location="product-guidance" className="font-mono text-[15px] text-white underline decoration-copper underline-offset-4 transition-colors duration-150 ease-precise hover:text-cta" data-id="element-222" data-mp="33afvykk8wgx0">
                  {CONTACT.techPhoneLabel}
                </a>
              </dd>
            </div>
            <div className="flex flex-col gap-1.5 py-4 sm:flex-row sm:items-center sm:gap-4" data-id="element-223" data-mp="26kgc3tyn9vz4">
              <dt className="flex items-center gap-3 font-mono text-[10px] tracking-label text-aluminum/70 sm:w-40 sm:shrink-0" data-id="element-224" data-mp="2bj9otk63xiy8">
                <MailIcon aria-hidden="true" className="h-4 w-4 text-copper" data-id="element-225" data-mp="21npyddabbcgv" />
                EMAIL
              </dt>
              <dd className="min-w-0" data-id="element-226" data-mp="1i2fzfe8mujn3">
                <a href={CONTACT.emailHref} data-analytics="email-click" data-analytics-location="product-guidance" className="inline-block max-w-full whitespace-nowrap text-[15px] text-white underline decoration-copper underline-offset-4 transition-colors duration-150 ease-precise hover:text-cta" data-id="element-227" data-mp="1i3havjtk985c">
                  {CONTACT.email}
                </a>
              </dd>
            </div>
            <div className="flex flex-col gap-1.5 py-4 sm:flex-row sm:items-baseline sm:gap-4" data-id="element-228" data-mp="1wy5ppokupixm">
              <dt className="flex items-center gap-3 font-mono text-[10px] leading-relaxed tracking-label text-aluminum/70 sm:w-40 sm:shrink-0" data-id="element-229" data-mp="2aor1ktbw0sm0">
                <MapPinIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-copper" data-id="element-230" data-mp="2ko9hmyoofu8s" />
                MAILING ADDRESS
              </dt>
              <dd className="min-w-0 text-[14.5px] leading-relaxed text-aluminum" data-id="element-231" data-mp="2fnc2yw2vlic1">
                {CONTACT.mailingAddress}
              </dd>
            </div>
          </dl>
        </div>

        <div className="border border-white/10 bg-white p-6 sm:p-9" data-id="element-232" data-mp="15e0bepc0qgz2">
          <form onSubmit={handleSubmit} noValidate data-id="element-233" data-mp="1acbjauir6hen">
            {errorList.length > 0 ? <div ref={summaryRef} tabIndex={-1} role="alert" className="mb-7 border-l-2 border-cta bg-warm p-4" data-id="element-234" data-mp="1f8sajxl91bvy">
                <p className="text-[14px] font-semibold text-navy" data-id="element-235" data-mp="mmh7mga66yvz">
                  Please complete {errorList.length} required{' '}
                  {errorList.length === 1 ? 'field' : 'fields'} before
                  continuing.
                </p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-[13.5px] text-graphite/85" data-id="element-236" data-mp="3v1925vly63gj">
                  {errorList.map(({
                key,
                label
              }) => <li key={key} data-id="element-237" data-mp="3tn2ee2cml4y">
                      <a href={`#field-${key}`} className="underline underline-offset-2" data-id="element-238" data-mp="3slj5mb6isc5">
                        {label}
                      </a>
                      : {errors[key]}
                    </li>)}
                </ul>
              </div> : null}

            <p className="font-mono text-[10px] tracking-label text-copper" data-id="element-239" data-mp="234n88ugwgo58">
              REQUEST FORM · REQUIRED FIELDS MARKED (REQUIRED)
            </p>

            <div className="mt-6 grid gap-5 sm:grid-cols-2" data-id="element-240" data-mp="1tfvj0u384hy0">
              <Field id="name" label="Name" required value={values.name} error={errors.name} onChange={v => update('name', v)} autoComplete="name" data-id="element-241" data-mp="1tec0sxy0bwtl" />
              <Field id="company" label="Company" required value={values.company} error={errors.company} onChange={v => update('company', v)} autoComplete="organization" data-id="element-242" data-mp="1fej6ytr6p0he" />
              <Field id="email" label="Work email" type="email" required value={values.email} error={errors.email} onChange={v => update('email', v)} autoComplete="email" data-id="element-243" data-mp="skqm364vr2fo" />
              <Field id="phone" label="Phone" type="tel" value={values.phone} onChange={v => update('phone', v)} autoComplete="tel" data-id="element-244" data-mp="em0nfm2ewup7" />

              <SelectField id="material" label="Material" required value={values.material} error={errors.material} onChange={v => update('material', v)} options={['Aluminum / 6061-T6', 'Copper', 'Soft aluminum alloy', 'Titanium', 'Other']} data-id="element-245" data-mp="imudom3bull9" />
              <SelectField id="holderSize" label="Holder size" required value={values.holderSize} error={errors.holderSize} onChange={v => update('holderSize', v)} options={['1 inch', '3/4 inch', 'Not sure']} data-id="element-246" data-mp="1slbx76mgeztl" />

              <Field id="machine" label="Machine model" value={values.machine} onChange={v => update('machine', v)} data-id="element-247" data-mp="2q82khzg8fkl1" />
              <Field id="horsepower" label="Machine horsepower" value={values.horsepower} onChange={v => update('horsepower', v)} data-id="element-248" data-mp="2gi984w89n1fq" />
              <Field id="workholding" label="Workholding / setup" value={values.workholding} onChange={v => update('workholding', v)} data-id="element-249" data-mp="390dgragdska6" />
              <Field id="quantity" label="Expected quantity" value={values.quantity} onChange={v => update('quantity', v)} data-id="element-250" data-mp="3dxagm3nofe58" />

              <div className="sm:col-span-2" data-id="element-251" data-mp="34xvshauzffnw">
                <Field id="goal" label="Application goal" required value={values.goal} error={errors.goal} onChange={v => update('goal', v)} data-id="element-252" data-mp="2zx80alv0oss3" />
              </div>
              <div className="sm:col-span-2" data-id="element-253" data-mp="2l83ktcflx2no">
                <label htmlFor="field-message" className="block text-[13px] font-semibold text-navy" data-id="element-254" data-mp="2l9eprfksgggd">
                  Message
                </label>
                <textarea id="field-message" name="message" rows={4} value={values.message} onChange={e => update('message', e.target.value)} className={inputClass} data-id="element-255" data-mp="2c7xafzf7gisq" />
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-256" data-mp="15ovve84n5wen">
              <button type="submit" data-analytics="product-guidance-cta" data-analytics-location="form-submit" className="inline-flex min-h-[52px] items-center justify-center bg-cta px-7 text-[14.5px] font-semibold text-navy transition-colors duration-150 ease-precise hover:bg-navy hover:text-white" data-id="element-257" data-mp="15mrrvranmzew">
                Request Product Guidance
              </button>
              <button type="button" onClick={handleCopy} className="inline-flex min-h-[52px] items-center justify-center gap-2 border border-rule px-6 text-[14px] font-medium text-navy transition-colors duration-150 ease-precise hover:border-copper hover:text-copper" data-id="element-258" data-mp="3lgs3nr04qm6j">
                <CopyIcon aria-hidden="true" className="h-4 w-4" data-id="element-259" data-mp="1ryugr5yfne8m" />
                Copy Request
              </button>
            </div>

            <p aria-live="polite" className="mt-3 text-[13px] text-graphite/80" data-id="element-260" data-mp="20xzjkoh11tql">
              {copyState === 'copied' ? 'Request copied to the clipboard.' : copyState === 'failed' ? 'Copy failed. Select the text of your entries, or email the details directly.' : ''}
            </p>

            <p className="mt-3 text-[13px] leading-relaxed text-graphite/75" data-id="element-261" data-mp="2om8vmyofv34j">
              Submitting opens your email app so you can review and send the
              request.
            </p>
            <p className="mt-2 text-[13px] leading-relaxed text-graphite/75" data-id="element-262" data-mp="uaapx6pc8pde">
              No email app configured? Send the details to{' '}
              <a href={CONTACT.emailHref} data-analytics="email-click" data-analytics-location="form-fallback" className="font-medium text-navy underline decoration-copper underline-offset-2" data-id="element-263" data-mp="qaa7zc315w1l">
                {CONTACT.email}
              </a>{' '}
              or call{' '}
              <a href={CONTACT.phoneHref} data-analytics="phone-click" data-analytics-location="form-fallback" className="font-medium text-navy underline decoration-copper underline-offset-2" data-id="element-264" data-mp="bbzffz8d5087">
                {CONTACT.phoneLabel}
              </a>
              .
            </p>
          </form>
        </div>
      </div>
    </section>;
}
interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  type?: string;
  error?: string;
  autoComplete?: string;
}
function Field({
  id,
  label,
  value,
  onChange,
  required = false,
  type = 'text',
  error,
  autoComplete
}: FieldProps) {
  const fieldId = `field-${id}`;
  const errorId = `${fieldId}-error`;
  return <div data-id="element-265" data-mp="1gambyrau590r">
      <label htmlFor={fieldId} className="block text-[13px] font-semibold text-navy" data-id="element-266" data-mp="1gea3xm0mtndd">
        {label}
        {required ? <span className="ml-1.5 font-mono text-[10px] font-normal tracking-label text-copper" data-id="element-267" data-mp="16et07a4xxjk1">
            (REQUIRED)
          </span> : null}
      </label>
      <input id={fieldId} name={id} type={type} value={value} autoComplete={autoComplete} aria-required={required} aria-invalid={error ? true : undefined} aria-describedby={error ? errorId : undefined} onChange={e => onChange(e.target.value)} className={`${inputClass} ${error ? 'border-cta' : ''}`} data-id="element-268" data-mp="243kzmp34krkn" />
      {error ? <p id={errorId} className="mt-1.5 text-[12.5px] font-medium text-navy" data-id="element-269" data-mp="2i1j678b7fjpl">
          {error}
        </p> : null}
    </div>;
}
interface SelectFieldProps extends Omit<FieldProps, 'type'> {
  options: string[];
}
function SelectField({
  id,
  label,
  value,
  onChange,
  options,
  required = false,
  error
}: SelectFieldProps) {
  const fieldId = `field-${id}`;
  const errorId = `${fieldId}-error`;
  return <div data-id="element-270" data-mp="34a856e7i9hsr">
      <label htmlFor={fieldId} className="block text-[13px] font-semibold text-navy" data-id="element-271" data-mp="34azu566lkha2">
        {label}
        {required ? <span className="ml-1.5 font-mono text-[10px] font-normal tracking-label text-copper" data-id="element-272" data-mp="3damudonlbh1j">
            (REQUIRED)
          </span> : null}
      </label>
      <select id={fieldId} name={id} value={value} aria-required={required} aria-invalid={error ? true : undefined} aria-describedby={error ? errorId : undefined} onChange={e => onChange(e.target.value)} className={`${inputClass} ${error ? 'border-cta' : ''}`} data-id="element-273" data-mp="3i1v3skox72ep">
        <option value="" data-id="element-274" data-mp="l6ooxdsh2boj">Select…</option>
        {options.map(option => <option key={option} value={option} data-id="element-275" data-mp="l8rr0w6wfv9m">
            {option}
          </option>)}
      </select>
      {error ? <p id={errorId} className="mt-1.5 text-[12.5px] font-medium text-navy" data-id="element-276" data-mp="v4mbzxyli2fk">
          {error}
        </p> : null}
    </div>;
}